import { DataPageModel } from "@modelve/api/models/dataPageModel";
import { PagedReportDataModel } from "@modelve/api/models/scenarioReportModels";
import { wrapApiError } from "../apiErrors";
import useSWR, { type SWRResponse } from "swr";
import { createApiBatchKey, createResourceApiKey } from "../cache/apiKey";
import type { ApiKey } from "../cache/types";
import { client } from "../client";
import type { ApiResourceRoute } from "../resources/types";
import { useApiStore } from "../store";
import {
  getRequestConfig,
  getSWRConfig,
  mergeApiQueryConfig,
} from "./queryConfig";
import type { ApiQueryConfig } from "./types";

export function usePagedApiGet<TEntity>(
  resourceRoutes: readonly ApiResourceRoute<TEntity>[],
  config?: ApiQueryConfig,
): SWRResponse<TEntity[][]>;

export function usePagedApiGet<TEntity>(
  resourceRoute: ApiResourceRoute<TEntity> | null | undefined,
  config?: ApiQueryConfig,
): SWRResponse<TEntity[]>;

export function usePagedApiGet<TEntity>(
  resourceRoute:
    | ApiResourceRoute<TEntity>
    | readonly ApiResourceRoute<TEntity>[]
    | null
    | undefined,
  config?: ApiQueryConfig,
): SWRResponse<TEntity[] | TEntity[][]> {
  useApiStore();
  const { disableCache } = mergeApiQueryConfig(config);
  const key = Array.isArray(resourceRoute)
    ? createApiBatchKey(resourceRoute, "data", "pagedGet")
    : createResourceApiKey(
        resourceRoute as ApiResourceRoute<TEntity> | null | undefined,
        "pagedGet",
      );

  return useSWR(
    key,
    async ({ requestedRoute }: ApiKey) => {
      if (typeof requestedRoute === "string")
        return getPagedApiResults<TEntity>(requestedRoute, disableCache);
      return Promise.all(
        requestedRoute.map((route) =>
          getPagedApiResults<TEntity>(route, disableCache),
        ),
      );
    },
    getSWRConfig(config),
  );
}

export async function getPagedApiResults<TEntity>(
  route: string,
  disableCache = true,
) {
  let nextToken: string | undefined;
  let nextPage = false;
  let page = 1;
  let results: TEntity[] = [];

  do {
    let pageParam = "";
    const separator = route.includes("?") ? "&" : "?";
    if (nextToken) {
      pageParam = `${separator}nextPageToken=${nextToken}`;
    } else if (page > 1) {
      pageParam = `${separator}page=${page}`;
    }
    const pagedRoute = `${route}${pageParam}`;

    const data = await client()
      .get<DataPageModel<TEntity> | PagedReportDataModel<TEntity>>(
        pagedRoute,
        getRequestConfig(disableCache),
      )
      .catch(function (error: unknown) {
        throw wrapApiError(error);
      });

    if ("data" in data.data) {
      results = results.concat(data.data.data);
      page++;
    }

    if ("items" in data.data) {
      results = results.concat(data.data.items);
      nextToken = data.data.paginationToken;
    }

    nextPage = data.data.hasNextPage;
  } while (nextPage);

  return results;
}
